import { t } from "i18next";
import { ChevronsUpDown } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "../button";
import { Separator } from "../separator";
import UserMenu from "./ProfileDropdown";
import ClientLoadedIndicator from "../tenant-client-selector/ClientLoadedIndicator";

import { OnlineResultsLogoSmall } from "@/components/icons/OnlineResultsLogo";
import Container from "@/components/layout/containers/Container";
import {
  ClientSelectorTrigger,
  TenantClientSelectorTrigger,
} from "@/components/ui/v2/tenant-client-selector";
import { ROUTES } from "@/constants/routes";
import { CaptainTopbarControl } from "@/features/captain-billing/components/CaptainTopbarControl";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import { UserLanguageProvider } from "@/lib/contexts/User/UserLanguageProvider";
import { useAuth } from "@/lib/hooks/useAuth";
import { useTenants } from "@/lib/hooks/useTenants";

const Topbar = () => {
  const { role, setTenantId, tenantId, userId, context } = useAuth();
  const { tenant, tenantLoading, addTenant } = useTenants();
  const { clientId } = useParams();
  const navigate = useNavigate();
  return (
    <Container className="flex-row p-4 bg-card dark:bg-background/80 dark:backdrop-blur-sm border-b border-border text-topbar-foreground">
      <div className="flex justify-between w-full items-center">
        <div className="flex gap-2 items-center h-full">
          <OnlineResultsLogoSmall
            className="size-7 cursor-pointer"
            onClick={() => {
              if (context === "admin") {
                navigate(`/${ROUTES.clients}`);
                return;
              }
              navigate(`/${ROUTES.campaigns}`);
            }}
          />
          {role === "SuperAdmin" && (
            <>
              <Separator orientation="vertical" />
              <TenantClientSelectorTrigger
                trigger={
                  <Button
                    variant="ghost"
                    className="flex gap-2 items-center hover:opacity-80 transition-opacity text-sm"
                    disabled={tenantLoading}
                  >
                    {tenantLoading ? (
                      <>
                        <span className="animate-pulse text-muted-foreground">
                          Loading...
                        </span>
                        <ChevronsUpDown className="size-3 opacity-50" />
                      </>
                    ) : (
                      <>
                        {tenant?.name}
                        <ChevronsUpDown className="size-3 opacity-50" />
                      </>
                    )}
                  </Button>
                }
                addTenant={addTenant}
                setTenantId={setTenantId}
                tenantId={tenantId ?? ""}
              />
            </>
          )}
          {role === "Admin" && (
            <>
              <Separator orientation="vertical" />
              <ClientSelectorTrigger
                trigger={
                  <Button
                    variant="ghost"
                    className="flex gap-2 items-center hover:opacity-80 transition-opacity text-sm"
                  >
                    {t("common.clients", { defaultValue: "Clients" })}
                    <ChevronsUpDown className="size-3 opacity-50" />
                  </Button>
                }
              />
            </>
          )}
          {clientId && (
            <>
              <Separator orientation="vertical" />
              <ClientLoadedIndicator />
            </>
          )}
        </div>
        <div className="flex gap-2 items-center h-full">
          <CaptainTopbarControl />
          <NotificationBell />
          <Separator orientation="vertical" />
          {userId ? (
            <UserLanguageProvider id={userId}>
              <UserMenu />
            </UserLanguageProvider>
          ) : (
            <UserMenu />
          )}
        </div>
      </div>
    </Container>
  );
};

export default Topbar;
